// Shared constants

export * from './marketing-taxonomy';
export * from './taxonomy-mapping';

import { ObjectConnectionRole } from '../types';

// Pagination defaults
export const DEFAULT_PAGE_LIMIT = 20;
export const MAX_PAGE_LIMIT = 100;

// File upload limits
export const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
export const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
export const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/webm'];

// Validation limits
export const MAX_COMMENT_LENGTH = 1000;
export const MAX_BIO_LENGTH = 500;
export const MAX_COLLECTION_NAME_LENGTH = 100;
export const MAX_QUIZ_TITLE_LENGTH = 200;

// Cache durations (in seconds)
export const CACHE_DURATION = {
  SHORT: 60, // 1 minute
  MEDIUM: 300,
  LONG: 3600, // 1 hour
  DAY: 86400, // 24 hours
} as const;

// Query cache configuration (in milliseconds)
export const DEFAULT_QUERY_CACHE_STALE_TIME = 1000 * 5; // 5 seconds

// Reaction types
export const REACTION_TYPES = [
  'love',
  'yum',
  'fire',
  'star',
  'bookmark',
  'curious',
  'meh',
  'nope',
] as const;

// Entity types with labels
export const ENTITY_TYPES = [
  { value: 'brand', label: 'Brand' },
  { value: 'organisation', label: 'Organisation' },
  { value: 'person', label: 'Person' },
  { value: 'space', label: 'Space' },
  { value: 'location', label: 'Location' },
  { value: 'group', label: 'Group' },
] as const;

// Helper to get entity type label by value
export const getEntityTypeLabel = (value: string): string => {
  return ENTITY_TYPES.find((type) => type.value === value)?.label || value;
};

// Activity types
export const ACTIVITY_TYPES = [
  'reaction',
  'comment',
  'vote',
  'submission',
  'collection_add',
  'collection_create',
  'points_add',
  'points_remove',
  'points_transfer',
  'points_receive',
  'points_spend',
] as const;

// Activity type filter options (label/value pairs for filter UI)
export const ACTIVITY_TYPE_OPTIONS = [
  { value: 'reaction', label: 'Reaction' },
  { value: 'comment', label: 'Comment' },
  { value: 'vote', label: 'Vote' },
  { value: 'submission', label: 'Submission' },
  { value: 'collection_add', label: 'Collection Add' },
  { value: 'collection_create', label: 'Collection Create' },
  { value: 'points_add', label: 'Points Add' },
  { value: 'points_remove', label: 'Points Remove' },
  { value: 'points_transfer', label: 'Points Transfer' },
  { value: 'points_receive', label: 'Points Receive' },
  { value: 'points_spend', label: 'Points Spend' },
] as const;

// Gender options
export const GENDER_OPTIONS = [
  { code: 'male', label: 'Male' },
  { code: 'female', label: 'Female' },
  { code: 'prefer-not-to-say', label: 'Prefer not to say' },
] as const;

// NZ Regions (sorted top to bottom as they appear on the map)
export const NZ_REGIONS = [
  {
    id: 'NZ-NTL',
    name: 'Northland',
  },
  {
    id: 'NZ-AUK',
    name: 'Auckland',
  },
  {
    id: 'NZ-WKO',
    name: 'Waikato',
  },
  {
    id: 'NZ-BOP',
    name: 'Bay of Plenty',
  },
  {
    id: 'NZ-GIS',
    name: 'Gisborne District',
  },
  {
    id: 'NZ-HKB',
    name: "Hawke's Bay",
  },
  {
    id: 'NZ-TKI',
    name: 'Taranaki',
  },
  {
    id: 'NZ-MWT',
    name: 'Manawatu-Wanganui',
  },
  {
    id: 'NZ-WGN',
    name: 'Wellington',
  },
  {
    id: 'NZ-TAS',
    name: 'Tasman District',
  },
  {
    id: 'NZ-NSN',
    name: 'Nelson City',
  },
  {
    id: 'NZ-MBH',
    name: 'Marlborough District',
  },
  {
    id: 'NZ-WTC',
    name: 'West Coast',
  },
  {
    id: 'NZ-CAN',
    name: 'Canterbury',
  },
  {
    id: 'NZ-OTA',
    name: 'Otago',
  },
  {
    id: 'NZ-STL',
    name: 'Southland',
  },
] as const;

// Status types
export const QUIZ_STATUSES = ['draft', 'active', 'archived'] as const;
export const USER_STATUSES = ['pending', 'approved', 'rejected', 'banned'] as const;
export const INITIATIVE_STAGES = [
  'draft',
  'proposal',
  'testing',
  'planning',
  'scheduled',
  'active',
  'completed',
  'archived',
] as const;

export const INITIATIVE_PROPOSAL_STATUS = [
  'draft',
  'submitted',
  'assessing',
  'accepted',
  'rejected',
] as const;

// Entity visibility options
export const ENTITY_VISIBILITY_OPTIONS = [
  { value: 'visible', label: 'Visible' },
  { value: 'hidden', label: 'Hidden' },
] as const;

// Item status options
export const ITEM_STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'disabled', label: 'Disabled' },
] as const;

// Initiative status options
export const INITIATIVE_STAGE_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'proposal', label: 'Proposal' },
  { value: 'gauging', label: 'Gauging Interest' },
  { value: 'planning', label: 'Planning' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'active', label: 'Active' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'archived', label: 'Archived' },
] as const;

export const INITIATIVE_PROPOSAL_STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'assessing', label: 'Assessing' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'rejected', label: 'Rejected' },
] as const;

// Role category groupings for onboarding UI
export const ROLE_CATEGORIES = [
  { id: 'brands', label: 'Brands & Producers' },
  { id: 'retailers', label: 'Retailers & Foodservice' },
  { id: 'supply-chain', label: 'Supply Chain & Distribution' },
  { id: 'hosts-places', label: 'Hosts, Workplaces & Venues' },
  { id: 'services', label: 'Services & Agencies' },
] as const;

export type RoleCategoryId = (typeof ROLE_CATEGORIES)[number]['id'];

// Role options with type information
export const ROLE_OPTIONS: readonly {
  id: string;
  label: string;
  description?: string;
  type: string[];
  category: RoleCategoryId;
}[] = [
  { id: 'brand', label: 'Brand', type: ['user', 'entity'], category: 'brands' },
  {
    id: 'primary-producer',
    label: 'Primary Producer',
    type: ['user', 'entity'],
    category: 'brands',
  },
  { id: 'manufacturer', label: 'Manufacturer', type: ['user', 'entity'], category: 'brands' },
  {
    id: 'contract-manufacturer',
    label: 'Contract Manufacturer',
    type: ['user', 'entity'],
    category: 'brands',
  },
  {
    id: 'contract-packaer',
    label: 'Contract Packer',
    type: ['user', 'entity'],
    category: 'brands',
  },
  {
    id: 'commercial-kitchen',
    label: 'Commercial Kitchen',
    type: ['user', 'entity'],
    category: 'brands',
  },

  {
    id: 'grocery-retailer',
    label: 'Grocery Retailer',
    type: ['user', 'entity'],
    category: 'retailers',
  },
  {
    id: 'liquor-retailer',
    label: 'Liquor Retailer',
    type: ['user', 'entity'],
    category: 'retailers',
  },
  { id: 'supermarket', label: 'Supermarket', type: ['user', 'entity'], category: 'retailers' },
  {
    id: 'other-retailer',
    label: 'Retailer (non-grocery)',
    type: ['user', 'entity'],
    category: 'retailers',
  },
  {
    id: 'foodservice-business',
    label: 'Foodservice Business',
    type: ['user', 'entity'],
    category: 'retailers',
  },
  {
    id: 'foodservice-supplier',
    label: 'Foodservice Supplier',
    type: ['user', 'entity'],
    category: 'retailers',
  },
  {
    id: 'hospitality-operator',
    label: 'Hospitality Operator',
    description: 'Eatery',
    type: ['user', 'entity'],
    category: 'retailers',
  },

  { id: 'importer', label: 'Importer', type: ['user', 'entity'], category: 'supply-chain' },
  { id: 'distributor', label: 'Distributor', type: ['user', 'entity'], category: 'supply-chain' },
  {
    id: 'broker',
    label: 'Broker / Sales Agency',
    type: ['user', 'entity'],
    category: 'supply-chain',
  },
  { id: 'wholesaler', label: 'Wholesaler', type: ['user', 'entity'], category: 'supply-chain' },
  {
    id: 'merchandiser',
    label: 'Merchandising Services',
    type: ['user', 'entity'],
    category: 'supply-chain',
  },
  {
    id: 'ingredients-supplier',
    label: 'Ingredients Supplier',
    type: ['user', 'entity'],
    category: 'supply-chain',
  },

  { id: 'host', label: 'Host', type: ['user', 'entity'], category: 'hosts-places' },
  {
    id: 'workplace',
    description: 'Product and activations at my workplace',
    label: 'Workplace',
    type: ['user', 'entity'],
    category: 'hosts-places',
  },
  {
    id: 'venue',
    label: 'Venue',
    description: 'Attraction, campus, market, event',
    type: ['user', 'entity'],
    category: 'hosts-places',
  },
  {
    id: 'university',
    label: 'University',
    description: 'University, campus location',
    type: ['user', 'entity'],
    category: 'hosts-places',
  },
  {
    id: 'space',
    description: 'Space available for use/hire',
    label: 'Space',
    type: ['user', 'entity'],
    category: 'hosts-places',
  },

  {
    id: 'service-provider',
    label: 'Service Provider',
    type: ['user', 'entity'],
    category: 'services',
  },
  {
    id: 'business-association',
    label: 'Business Association',
    type: ['user', 'entity'],
    category: 'services',
  },
  {
    id: 'business-network',
    label: 'Business Network',
    type: ['user', 'entity'],
    category: 'services',
  },
  {
    id: 'experiential-agency',
    label: 'Experiential / Activations Agency',
    type: ['entity'],
    category: 'services',
  },
  { id: 'marketing-agency', label: 'Marketing Agency', type: ['entity'], category: 'services' },
  {
    id: 'exhibition-organiser',
    label: 'Exhibition Organiser',
    type: ['entity'],
    category: 'services',
  },
  {
    id: 'government-agency',
    label: 'Government Agency',
    type: ['user', 'entity'],
    category: 'services',
  },
  {
    id: 'software-platform',
    label: 'Software / SaaS Platform',
    description: 'Technology platform or app for trade, retail or hospitality',
    type: ['user', 'entity'],
    category: 'services',
  },
];

export const CAPABILITY_OPTIONS = [
  { id: 'product-development', label: 'Product Development' },
  { id: 'product-formulation', label: 'Product Formulation' },
  { id: 'recipe-development', label: 'Recipe Development' },
  { id: 'new-product-innovation', label: 'New Product Innovation' },
  { id: 'reformulation', label: 'Reformulation / Optimisation' },

  { id: 'manufacturing-scale-up', label: 'Manufacturing Scale-Up' },
  { id: 'pilot-production', label: 'Pilot / Small Batch Production' },
  { id: 'full-scale-manufacturing', label: 'Full Scale Manufacturing' },
  { id: 'co-packing', label: 'Co-Packing / Packing' },

  { id: 'sourcing-ingredients', label: 'Ingredient Sourcing' },
  { id: 'supplier-management', label: 'Supplier Management' },

  { id: 'quality-assurance', label: 'Quality Assurance' },
  { id: 'food-safety', label: 'Food Safety & Compliance' },
  { id: 'regulatory-compliance', label: 'Regulatory Compliance' },
  { id: 'labelling-nutrition', label: 'Labelling & Nutrition Panels' },
  { id: 'certification-support', label: 'Certification Support' },

  { id: 'packaging-development', label: 'Packaging Development' },
  { id: 'packaging-design', label: 'Packaging Design' },
  { id: 'sustainable-packaging', label: 'Sustainable Packaging Solutions' },

  { id: 'branding', label: 'Brand Strategy' },
  { id: 'brand-identity', label: 'Brand Identity & Design' },
  { id: 'creative-production', label: 'Creative Production' },

  { id: 'marketing-strategy', label: 'Marketing Strategy' },
  { id: 'digital-marketing', label: 'Digital Marketing' },
  { id: 'performance-marketing', label: 'Performance Marketing' },
  { id: 'social-media', label: 'Social Media Management' },

  { id: 'retail-strategy', label: 'Retail Strategy' },
  { id: 'category-management', label: 'Category Management' },
  { id: 'range-review-support', label: 'Range Review Support' },
  { id: 'buyer-pitching', label: 'Retail Buyer Pitching' },

  { id: 'distribution-setup', label: 'Distribution Setup' },
  { id: 'logistics-fulfilment', label: 'Logistics & Fulfilment' },
  { id: 'inventory-management', label: 'Inventory Management' },

  { id: 'export-strategy', label: 'Export Strategy' },
  { id: 'market-entry', label: 'Market Entry Support' },

  { id: 'ecommerce-setup', label: 'E-commerce Setup' },
  { id: 'd2c-operations', label: 'DTC Operations' },

  { id: 'activations-events', label: 'Activations & Events' },
  { id: 'sampling-programs', label: 'Sampling Programs' },

  { id: 'advertising-media', label: 'Advertising & Media Buying' },
  { id: 'out-of-home-advertising', label: 'Out-of-Home / DOOH Advertising' },
  { id: 'campaign-management', label: 'Campaign Planning & Management' },
  { id: 'programmatic-advertising', label: 'Programmatic Advertising' },
  { id: 'audience-targeting', label: 'Audience Targeting & Reach' },

  { id: 'data-insights', label: 'Data & Insights' },
  { id: 'consumer-research', label: 'Consumer Research' },

  { id: 'software-platform', label: 'Software Platform / SaaS' },
  { id: 'self-serve-tools', label: 'Self-Serve Tools' },
  { id: 'analytics-reporting', label: 'Analytics & Reporting Dashboards' },
  { id: 'platform-integrations', label: 'Integrations & API Access' },

  { id: 'fundraising', label: 'Fundraising Support' },
  { id: 'commercial-strategy', label: 'Commercial Strategy' },
] as const;

export const SPECIALISATION_OPTIONS = [
  { id: 'plant-based', label: 'Plant-Based' },
  { id: 'vegan', label: 'Vegan' },
  { id: 'vegetarian', label: 'Vegetarian' },
  { id: 'gluten-free', label: 'Gluten-Free' },
  { id: 'dairy-free', label: 'Dairy-Free' },
  { id: 'organic', label: 'Organic' },
  { id: 'non-gmo', label: 'Non-GMO' },

  { id: 'functional-foods', label: 'Functional Foods' },
  { id: 'supplements-nutraceuticals', label: 'Supplements / Nutraceuticals' },
  { id: 'sports-nutrition', label: 'Sports Nutrition' },

  { id: 'beverages', label: 'Beverages' },
  { id: 'alcoholic-beverages', label: 'Alcoholic Beverages' },
  { id: 'non-alcoholic-beverages', label: 'Non-Alcoholic Beverages' },

  { id: 'snacks', label: 'Snacks' },
  { id: 'confectionery', label: 'Confectionery' },
  { id: 'bakery', label: 'Bakery' },
  { id: 'frozen-foods', label: 'Frozen Foods' },
  { id: 'ready-meals', label: 'Ready Meals' },

  { id: 'fresh-produce', label: 'Fresh Produce' },
  { id: 'meat-seafood', label: 'Meat & Seafood' },
  { id: 'dairy', label: 'Dairy' },

  { id: 'private-label', label: 'Private Label' },
  { id: 'premium-products', label: 'Premium Products' },
  { id: 'value-products', label: 'Value / Mass Products' },

  { id: 'foodservice-products', label: 'Foodservice Products' },
  { id: 'retail-products', label: 'Retail FMCG Products' },

  { id: 'sustainability-focused', label: 'Sustainability Focused' },
  { id: 'clean-label', label: 'Clean Label' },
  { id: 'pet-food', label: 'Pet Food' },
  { id: 'pet-supplements', label: 'Pet Supplements' },

  { id: 'startup-brands', label: 'Startup / Emerging Brands' },
  { id: 'enterprise-brands', label: 'Enterprise / Large Brands' },

  { id: 'advertising-technology', label: 'Advertising Technology (AdTech)' },
  { id: 'digital-out-of-home', label: 'Digital Out-of-Home (DOOH)' },
  { id: 'retail-media', label: 'Retail Media' },
  { id: 'marketing-technology', label: 'Marketing Technology (MarTech)' },
  { id: 'ecommerce-platforms', label: 'E-commerce Platforms' },
  { id: 'hospitality-technology', label: 'Hospitality Technology' },
  { id: 'saas-platform', label: 'SaaS / Digital Platform' },
] as const;

export const CHANNEL_EXPERTISE_OPTIONS = [
  { id: 'grocery-multiples', label: 'Grocery Multiples (Supermarkets)' },
  { id: 'independent-retail', label: 'Independent Retail' },
  { id: 'convenience', label: 'Convenience Stores' },
  { id: 'liquor', label: 'Liquor Retail' },

  { id: 'foodservice', label: 'Foodservice' },
  { id: 'foodservice-qsr', label: 'QSR / Fast Food' },
  { id: 'foodservice-casual', label: 'Casual Dining' },
  { id: 'foodservice-premium', label: 'Premium Dining' },
  { id: 'cafes', label: 'Cafés' },
  { id: 'catering', label: 'Catering & Events' },

  { id: 'ecommerce-dtc', label: 'E-commerce (DTC)' },
  { id: 'ecommerce-marketplaces', label: 'Online Marketplaces' },

  { id: 'export-distributors', label: 'Export via Distributors' },
  { id: 'export-direct', label: 'Direct Export (Retail / Partners)' },
  { id: 'travel-retail', label: 'Travel Retail / Duty Free' },

  { id: 'events-festivals', label: 'Events & Festivals' },
  { id: 'pop-ups', label: 'Pop-ups & Activations' },

  { id: 'workplace-hospitality', label: 'Workplace / Corporate Hospitality' },

  { id: 'out-of-home-media', label: 'Out-of-Home Media (Billboards, Screens)' },
  { id: 'retail-media-networks', label: 'Retail Media Networks (In-Store Screens)' },
  { id: 'transit-media', label: 'Transit & Airport Media' },
  { id: 'digital-social-media', label: 'Digital & Social Media' },
] as const;

/**
 * Ordered groups for {@link BUSINESS_NEED_OPTIONS}. Each option references a
 * `group` id below so UIs can render the needs grouped (e.g. a grouped
 * multi-select) in this order.
 */
export const BUSINESS_NEED_GROUPS = [
  { id: 'grow-demand', label: 'Grow demand & awareness' },
  { id: 'product-supply', label: 'Product & supply' },
  { id: 'compliance-packaging', label: 'Compliance & packaging' },
  { id: 'retail-channels', label: 'Retail & channels' },
  { id: 'export-markets', label: 'Export & new markets' },
  { id: 'operations', label: 'Operations' },
  { id: 'commercial', label: 'Commercial & partnerships' },
  { id: 'people', label: 'People' },
  { id: 'on-sizzle', label: 'On Sizzle' },
] as const;

export type BusinessNeedGroupId = (typeof BUSINESS_NEED_GROUPS)[number]['id'];

export const BUSINESS_NEED_OPTIONS = [
  // Grow demand & awareness
  { id: 'branding', label: 'Branding & Positioning', group: 'grow-demand' },
  { id: 'marketing', label: 'Marketing & Growth Support', group: 'grow-demand' },
  { id: 'launch-strategy', label: 'Go-to-Market / Launch Strategy', group: 'grow-demand' },
  { id: 'sampling-activations', label: 'Run Sampling / Activations', group: 'grow-demand' },
  { id: 'events-opportunities', label: 'Participate in Events / Pop-ups', group: 'grow-demand' },
  { id: 'ecommerce-launch', label: 'Launch / Grow E-commerce (DTC)', group: 'grow-demand' },
  { id: 'market-insights', label: 'Consumer Insights & Market Trends', group: 'grow-demand' },
  { id: 'market-validation', label: 'Validate Product / Market Fit', group: 'grow-demand' },

  // Product & supply
  { id: 'develop-product', label: 'Develop a New Product', group: 'product-supply' },
  { id: 'improve-product', label: 'Improve / Reformulate Product', group: 'product-supply' },
  { id: 'find-manufacturer', label: 'Find Manufacturer / Co-packer', group: 'product-supply' },
  { id: 'scale-production', label: 'Scale Production', group: 'product-supply' },
  { id: 'source-ingredients', label: 'Source Ingredients / Suppliers', group: 'product-supply' },

  // Compliance & packaging
  {
    id: 'compliance-regulatory',
    label: 'Compliance & Regulatory Support',
    group: 'compliance-packaging',
  },
  {
    id: 'labelling-nutrition',
    label: 'Labelling & Nutrition Panels',
    group: 'compliance-packaging',
  },
  { id: 'packaging-development', label: 'Packaging Development', group: 'compliance-packaging' },

  // Retail & channels
  {
    id: 'get-into-retail',
    label: 'Get into Retail (Supermarkets / Stores)',
    group: 'retail-channels',
  },
  { id: 'connect-buyers', label: 'Connect with Retail Buyers', group: 'retail-channels' },
  { id: 'distribution', label: 'Find Distributor / Channel Partner', group: 'retail-channels' },
  {
    id: 'foodservice-expansion',
    label: 'Expand into Foodservice / Hospitality',
    group: 'retail-channels',
  },

  // Export & new markets
  { id: 'export-market-entry', label: 'Enter a New Market (Export)', group: 'export-markets' },

  // Operations
  { id: 'supply-chain', label: 'Supply Chain & Logistics Support', group: 'operations' },
  { id: 'operations-optimisation', label: 'Operations Optimisation', group: 'operations' },

  // Commercial & partnerships
  { id: 'funding', label: 'Raise Capital / Investment', group: 'commercial' },
  { id: 'partnerships', label: 'Find Partners / Collaborators', group: 'commercial' },

  // People
  { id: 'hiring', label: 'Hiring / Talent Acquisition', group: 'people' },

  // On Sizzle (important differentiator)
  { id: 'list-opportunities', label: 'List Opportunities / Initiatives', group: 'on-sizzle' },
  { id: 'offer-services', label: 'Offer Services to Others', group: 'on-sizzle' },
] as const satisfies readonly { id: string; label: string; group: BusinessNeedGroupId }[];

export type BusinessNeedId = (typeof BUSINESS_NEED_OPTIONS)[number]['id'];

// User role/interest options (filtered for users)
export const USER_ROLE_OPTIONS = ROLE_OPTIONS;

// Entity role options (filtered for entities)
export const ENTITY_ROLE_OPTIONS = ROLE_OPTIONS.filter((role) => role.type.includes('entity')).map(
  ({ id, label, description, category }) => ({ id, label, description, category })
);

// Quiz leaderboard visibility options
export const QUIZ_LEADERBOARD_VISIBILITY_OPTIONS = [
  { value: 'none', label: 'Disabled' },
  { value: 'public', label: 'Public' },
  { value: 'if_submitted', label: 'Only if made a submission' },
] as const;

export * from './date-preset';
export * from './initiatives';
export * from './content';
export * from './object-components';
export * from './themes';
export * from './playbooks';
export * from './site-settings';
export * from './auth';
export * from './tools';

// Message types
export const MESSAGE_TYPES = {
  GENERAL_ENQUIRY: 'general_enquiry',
  SAMPLE_REQUEST: 'sample_request',
  MEETING_REQUEST: 'meeting_request',
  CUSTOM: 'custom',
} as const;

export const THREAD_TYPES = {
  ENQUIRY: 'enquiry',
  GROUP_CHAT: 'group_chat',
  SUPPORT: 'support',
  TRADE: 'trade',
} as const;

export const MEETING_TYPES = {
  PHONE: 'phone',
  EMAIL: 'email',
  VIDEO: 'video',
  IN_PERSON: 'in_person',
} as const;

export const SAMPLE_REQUEST_STATUSES = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  FULFILLED: 'fulfilled',
} as const;

export const MEETING_REQUEST_STATUSES = {
  PENDING: 'pending',
  ACCEPTED: 'accepted',
  DECLINED: 'declined',
} as const;

export const TRADE_HUB_VISIBILITY = {
  PUBLIC: 'public',
  USER_TRADE_PARTNERS: 'user_trade_partners',
  APPROVED_PARTNERS: 'approved_partners',
  SPECIFIC_USERS_ONLY: 'specific_users_only',
} as const;

// Object connection roles (for resource user management)
export const OBJECT_CONNECTION_ROLES: ObjectConnectionRole[] = [
  {
    value: 'viewer',
    label: 'Viewer',
    description: 'Can view analytics',
    flags: ['dashboard.view'],
  },
  {
    value: 'editor',
    label: 'Editor',
    description: 'Can edit products and initiatives',
    flags: ['dashboard.view'],
  },
  {
    value: 'admin',
    label: 'Admin',
    description: 'Full access including user management',
    flags: ['dashboard.view'],
  },
] as const;

// Relationship options for entity-to-entity connections
export const RELATIONSHIP_OPTIONS = [
  { value: 'distributor', label: 'Distributor' },
  { value: 'broker', label: 'Broker' },
  { value: 'available_at', label: 'Available At' },
] as const;

// Relationship options for item-to-entity connections
export const ITEM_RELATIONSHIP_OPTIONS = [
  { value: 'available_at', label: 'Available At' },
  { value: 'distributed_by', label: 'Distributed By' },
  { value: 'manufactured_by', label: 'Manufactured By' },
  { value: 'supplied_by', label: 'Supplied By' },
] as const;

// Connection role options for entity-to-entity connections
export const CONNECTION_ROLE_OPTIONS = [{ value: 'partner', label: 'Partner' }] as const;

// Assignment role options for initiative assignments (users and entities)
// Supports multiple roles per assignment
export const ASSIGNMENT_ROLE_OPTIONS = [
  { value: 'host', label: 'Host' },
  { value: 'provider', label: 'Provider' },
  { value: 'sponsor', label: 'Sponsor' },
] as const;

export type AssignmentRole = (typeof ASSIGNMENT_ROLE_OPTIONS)[number]['value'];

export const FERNMARK_LOGO_URL =
  'https://www.manawahoney.co.nz/wp-content/uploads/2021/12/nz-fernmark-logo.png';
export const FERNMARK_LOGO_LARGE_URL =
  'https://www.manawahoney.co.nz/wp-content/uploads/2021/12/nz-fernmark-logo.png';
export const FERNMARK_LOGO_SMALL_URL =
  'https://images.ctfassets.net/pn8wbiqtnzw9/2AIYT2T3EES80UxRtf0sU1/99f99fcceeac91a01b573057bb439648/Fernmark.png?q=50&w=200&fm=webp';

// Membership constants
export const MEMBERSHIP_ROLES = [
  { value: 'member', label: 'Member' },
  { value: 'moderator', label: 'Moderator' },
  { value: 'admin', label: 'Admin' },
] as const;

export const MEMBERSHIP_STATUSES = [
  { value: 'active', label: 'Active' },
  { value: 'pending', label: 'Pending Approval' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'left', label: 'Left' },
] as const;

export const DEFAULT_MEMBERSHIP_SETTINGS: {
  enabled: boolean;
  autoApprove: boolean;
  roles: string[];
  capacity: number | null;
  showMemberCount: boolean;
  membersListPublic: boolean;
} = {
  enabled: false,
  autoApprove: true,
  roles: ['member'],
  capacity: null,
  showMemberCount: true,
  membersListPublic: true,
};

export const EnquiryTypes = {
  general: 'General Enquiry',
  sample_request: 'Sample Request',
  space: 'Space Enquiry',
  partnership: 'Partnership Enquiry',
  provider: 'Provider Enquiry',
  participation: 'Participation Enquiry',
  proposal_feedback: 'Proposal Feedback',
};
